<template>
  <v-container fill-height fluid grid-list-xl class="pt-0">
    <v-flex md12>
      <material-card color="green" title="Class Summary" class="grid-card">
        <v-layout row class="mt-2 mr-1">
          <v-select
            v-model="regID"
            label="Term"
            class="pl-3 width-250"
            :items="terms"
            hide-details
            @change="onTermChanged"
          ></v-select>
          <v-select
            v-model="gander"
            label="Gander"
            class="pl-3 width-250"
            :items="['Boys', 'Girls']"
            hide-details
            @change="onGanderChanged"
          ></v-select>
          <v-spacer></v-spacer>
          <download-excel
            :fields="exportFields()"
            :name="getExportFileName()"
            :before-generate="() => (isExporting = true)"
            :before-finish="() => (isExporting = false)"
            :fetch="exportToExcel"
            type="xls"
          >
            <v-btn small icon :loading="isExporting" class="mt-1 py-0"
              ><v-icon color="success">mdi-file-export</v-icon></v-btn
            >
          </download-excel>

          <v-btn-toggle
            class="toggle-button-group"
            mandatory
            v-model="showClasses"
          >
            <v-btn class="toggle-button" flat v-bind:value="false">
              Hide Classes
            </v-btn>
            <v-btn class="toggle-button" flat v-bind:value="true">
              Show Classes
            </v-btn>
          </v-btn-toggle>
        </v-layout>
        <vue-good-table
          class="pt-3"
          style="cursor: pointer;"
          ref="table"
          :columns="columns"
          :rows="classSummary"
          :groupOptions="{
            enabled: true,
            headerPosition: 'bottom',
          }"
          :search-options="{
            enabled: false,
          }"
        >
          <template slot="table-header-row" slot-scope="props">
            <div @click="openPrintClassListDialog(props.row, props.column)">
              <span>
                {{ props.formattedRow[props.column.field] }}
              </span>
            </div>
          </template>
          <template slot="table-row" slot-scope="props">
            <div @click="openPrintClassListDialog(props.row, props.column)">
              <span>
                {{ props.formattedRow[props.column.field] }}
              </span>
            </div>
          </template>
        </vue-good-table>
      </material-card>
    </v-flex>
    <v-dialog v-model="printClassListDialog" max-width="480">
      <v-card>
        <!-- Header -->
        <div class="dialog-header">
          <v-icon class="dialog-header-icon">mdi-printer</v-icon>
          <span class="dialog-header-title">Print Options</span>
          <v-spacer></v-spacer>
          <v-btn class="my-0" icon flat small @click="closePrintClassDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Class Info chips -->
        <v-card-text class="pt-4 pb-2">
          <div class="dialog-info-row">
            <div class="dialog-info-chip" v-if="printClassDialog.grade">
              <v-icon small class="mr-1">mdi-school</v-icon>
              <span class="dialog-info-label">Grade</span>
              <span class="dialog-info-value">{{
                printClassDialog.grade
              }}</span>
            </div>
            <div class="dialog-info-chip" v-if="printClassDialog.teacher">
              <v-icon small class="mr-1">mdi-account</v-icon>
              <span class="dialog-info-label">Teacher</span>
              <span class="dialog-info-value">{{
                printClassDialog.teacher
              }}</span>
            </div>
            <div class="dialog-info-chip" v-if="printClassDialog.week">
              <v-icon small class="mr-1">mdi-calendar-week</v-icon>
              <span class="dialog-info-label">Week</span>
              <span class="dialog-info-value">{{ printClassDialog.week }}</span>
            </div>
            <div
              class="dialog-info-chip dialog-info-chip--count"
              v-if="printClassDialog.count != null"
            >
              <v-icon small class="mr-1">mdi-account-multiple</v-icon>
              <span class="dialog-info-label">Students</span>
              <span class="dialog-info-value">{{
                printClassDialog.count
              }}</span>
            </div>
          </div>

          <v-divider class="my-3"></v-divider>

          <!-- Print Class List section -->
          <div class="dialog-section-label">Class List</div>
          <v-btn
            block
            color="success"
            class="mb-1"
            :loading="isPrintingClassList"
            :disabled="isPrintingClassList"
            @click="printClassList"
          >
            <v-icon left>mdi-printer</v-icon>
            Print Class List
          </v-btn>
          <v-alert
            :value="!!classListError"
            type="error"
            class="mt-2 mb-3"
            @input="classListError = ''"
          >
            {{ classListError }}
          </v-alert>

          <!-- Print Labels section -->
          <div class="dialog-section-label mb-0">Labels (30 per sheet)</div>
          <v-layout row wrap>
            <v-flex xs6 class="pr-1">
              <v-btn
                block
                color="success"
                :loading="isPrintingLabels"
                :disabled="isPrintingLabels"
                @click="printLabels('full')"
              >
                                <v-icon left>mdi-label</v-icon>
                                Detailed Labels
              </v-btn>
            </v-flex>
            <v-flex xs6 class="pl-1">
              <v-btn
                block
                color="success"
                outline
                :loading="isPrintingLabels"
                :disabled="isPrintingLabels"
                @click="printLabels('nameOnly')"
              >
                <v-icon left>mdi-label-outline</v-icon>
                Name Only
              </v-btn>
            </v-flex>
          </v-layout>

          <v-alert
            :value="!!labelError"
            type="error"
            class="mt-3 mb-0"
            @input="labelError = ''"
          >
            {{ labelError }}
          </v-alert>
        </v-card-text>

        <v-card-actions class="pt-0 pb-3 pr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="closePrintClassDialog"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { pluralize, sum } from "../utils/helpers.js";
import moment from "moment";

export default {
  data: () => ({
    classSummary: [],
    printClassListDialog: false,
    printForClass: "",
    showClasses: true,
    regID: "",
    gander: "Boys",
    terms: [],
    isExporting: false,
    isPrintingLabels: false,
    isPrintingClassList: false,
    labelError: "",
    classListError: "",

    printClassDialog: {
      classID: "",
      teacher: "",
      week: "",
      grade: "",
      weekDisplay: "",
      count: null,
      weekLabel: "",
    },

    columns: [
      {
        sortable: false,
        label: "Grade",
        field: "class",
        width: "200px",
        hidden: false,
      },
      {
        sortable: false,
        label: "Teacher",
        field: "teacher",
        width: "200px",
        hidden: false,
      },
      {
        sortable: false,
        label: "Week 1",
        field: "week1",
        width: "100px",
        type: "Number",
        hidden: false,
      },
      {
        sortable: false,
        label: "Week 2",
        field: "week2",
        width: "100px",
        type: "Number",
        hidden: false,
      },
      {
        sortable: false,
        label: "Week 3",
        field: "week3",
        width: "100px",
        type: "Number",
        hidden: false,
      },
      {
        sortable: false,
        label: "Week 4",
        field: "week4",
        width: "100px",
        type: "Number",
        hidden: false,
      },
      {
        sortable: false,
        label: "Week 5",
        field: "week5",
        width: "100px",
        type: "Number",
        hidden: false,
      },
      {
        sortable: false,
        label: "Week 6",
        field: "week6",
        width: "100px",
        type: "Number",
        hidden: false,
      },
      {
        sortable: false,
        label: "Week 7",
        field: "week7",
        width: "100px",
        type: "Number",
        hidden: false,
      },
      {
        sortable: false,
        label: "Week 8",
        field: "week8",
        width: "100px",
        type: "Number",
        hidden: false,
      },
      {
        sortable: false,
        label: "Week 9",
        field: "week9",
        width: "100px",
        type: "Number",
        hidden: false,
      },
    ],
  }),
  watch: {
    showClasses(newValue, oldValue) {
      this.classSummary
        .map((c) => c.children)
        .forEach((grade) =>
          grade.forEach((row) => {
            if (!row.teacher) return;

            row.hidden = !newValue;
          })
        );
    },
  },
  async created() {
    await this.loadRegID();

    this.gander = localStorage.getItem("lapidus-class-summary-gander");

    this.loadData();
    this.loadTerms();
  },
  methods: {
    async loadData() {
      localStorage.setItem("lapidus-class-summary-gander", this.gander);

      const response = await this.$http.get(
        "api/lapidusDayCamp/GetClassSummary?setting=" + this.regID
      );

      const classSummary = response
        .filter((c) => {
          return this.gander == "Boys"
            ? c.class.startsWith("B")
            : c.class.startsWith("G");
        })
        .map((grades) => {
          grades.children.forEach((c) => (c.hidden = false));

          grades.class =
            grades.class +
            ` Total (${grades.children.length} ${pluralize(
              "Class",
              grades.children.length
            )})`;
          return grades;
        });

      classSummary.push({
        class: "Grand Total",
        teacher: "",
        week1: sum(classSummary.map((c) => c.week1)),
        week2: sum(classSummary.map((c) => c.week2)),
        week3: sum(classSummary.map((c) => c.week3)),
        week4: sum(classSummary.map((c) => c.week4)),
        week5: sum(classSummary.map((c) => c.week5)),
        week6: sum(classSummary.map((c) => c.week6)),
        week7: sum(classSummary.map((c) => c.week7)),
        week8: sum(classSummary.map((c) => c.week8)),
        week9: sum(classSummary.map((c) => c.week9)),
        children: [
          {
            hidden: true,
            class: "",
            teacher: "",
            week1: 1,
            week2: 1,
            week3: 1,
            week4: 1,
            week5: 1,
            week6: 1,
            week7: 1,
            week8: 1,
            week9: 1,
          },
        ],
      });

      this.classSummary = classSummary;
    },
    onTermChanged(value) {
      this.loadData();
    },
    onGanderChanged(value) {
      this.loadData();
    },
    async loadTerms() {
      const response = await this.$http.get("api/lapidusDayCamp/getRegTerms");

      this.terms = response.map((r) => r.term);
    },
    async loadRegID() {
      this.regID = await this.$http.get("api/lapidusDayCamp/getRegID");
    },
    openPrintClassListDialog(row, column) {
      this.printClassDialog.teacher = row["teacher"];
      this.printClassDialog.grade =
        row["class"].split(" ")[0] != "Grand" ? row["class"].split(" ")[0] : "";
      this.printClassDialog.classID = row["classID"]
        ? row["classID"]
        : undefined;

      const isWeekColumn = column.field.startsWith("week");
      this.printClassDialog.week = isWeekColumn
        ? column.field.substring(4)
        : "";
      this.printClassDialog.weekLabel = isWeekColumn ? column.label : "";
      this.printClassDialog.count =
        isWeekColumn && row[column.field] != null ? row[column.field] : null;

      this.printClassListDialog = true;
    },
    closePrintClassDialog() {
      this.printClassListDialog = false;

      this.printClassDialog.teacher = "";
      this.printClassDialog.grade = "";
      this.printClassDialog.classID = "";
      this.printClassDialog.week = "";
      this.printClassDialog.weekDisplay = "";
      this.printClassDialog.weekLabel = "";
      this.printClassDialog.count = null;
      this.classListError = "";
    },
    async printClassList() {
      this.isPrintingClassList = true;
      this.classListError = "";

      try {
        const payload = {
          classId: this.printClassDialog.classID || null,
          schoolYearId: this.regID || null,
          childIds: null,
          filters: this.getLabelFilters(),
        };

        const response = await this.$http.post(
          "api/lapidusDayCamp/GenerateClassListPdf",
          payload,
          {
            headers: { "Content-Type": "application/json" },
            responseType: "blob",
          }
        );

        const blob = new Blob([response], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download =
          "LapidusDayCampClassList_" + moment().format("YYYYMMDD") + ".pdf";
        link.click();
        window.URL.revokeObjectURL(link.href);

        this.$notify({
          group: "notification",
          text: "Class list downloaded successfully",
          type: "success",
        });

        this.closePrintClassDialog();
      } catch (e) {
        this.classListError = "Failed to generate class list. Please try again.";
      } finally {
        this.isPrintingClassList = false;
      }
    },
    async printLabels(labelMode) {
      this.isPrintingLabels = true;
      this.labelError = "";

      try {
        const payload = {
          classId: this.printClassDialog.classID || null,
          schoolYearId: this.regID || null,
          labelMode: labelMode,
          filters: this.getLabelFilters(),
        };

        const response = await this.$http.post(
          "api/lapidusDayCamp/GenerateClassLabelsPdf",
          payload,
          {
            headers: { "Content-Type": "application/json" },
            responseType: "blob",
          }
        );

        const blob = new Blob([response], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
                const modeLabel = labelMode === "full" ? "detailed" : labelMode;
                link.download =
                    "LapidusDayCampLabels_" +
                    modeLabel +
          "_" +
          moment().format("YYYYMMDD") +
          ".pdf";
        link.click();
        window.URL.revokeObjectURL(link.href);

        this.$notify({
          group: "notification",
          text: "Labels downloaded successfully",
          type: "success",
        });

        this.closePrintClassDialog();
      } catch (e) {
        this.labelError = "Failed to generate labels. Please try again.";
      } finally {
        this.isPrintingLabels = false;
      }
    },
    getLabelFilters() {
      const { grade, week } = this.printClassDialog;
      const filters = {};

      if (grade) {
        filters.grade = grade;
      }

      if (week) {
        filters.week = week;
      }

      // Always pass the current gender so the backend matches exactly what the grid shows.
      filters.gradePrefix = this.gander === "Boys" ? "B" : "G";

      return filters;
    },
    getExportFileName() {
      return "Class Summary - " + moment().format("L");
    },

    exportFields() {
      let fields = {};

      this.columns.forEach((column) => {
        if (column.hidden) return;

        fields[column.label] = column.field;
      });

      return fields;
    },

    async exportToExcel() {
      return this.classSummary;
    },
  },
};
</script>
<style>
.toggle-button {
  font-size: 15px;
  padding: 5px !important;
}
.toggle-button-group.v-btn-toggle .v-btn.v-btn--active {
  background-color: #dcdfe6;
}
.v-btn__content {
  color: unset;
}

/* Print Options Dialog */
.dialog-header {
  display: flex;
  align-items: center;
  padding: 12px 12px 12px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: #f9f9f9;
  border-radius: 4px 4px 0 0;
}
.dialog-header-icon {
  color: #4caf50;
  margin-right: 10px;
  font-size: 22px !important;
}
.dialog-header-title {
  font-size: 17px;
  font-weight: 600;
  color: #333;
  letter-spacing: 0.2px;
}
.dialog-info-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.dialog-info-chip {
  display: inline-flex;
  align-items: center;
  background: #f1f8f1;
  border: 1px solid #c8e6c9;
  border-radius: 20px;
  padding: 4px 12px 4px 8px;
  font-size: 13px;
}
.dialog-info-label {
  color: #777;
  margin-right: 5px;
}
.dialog-info-value {
  font-weight: 600;
  color: #333;
}
.dialog-info-chip--count {
  background: #e8f5e9;
  border-color: #4caf50;
}
.dialog-info-chip--count .dialog-info-value {
  color: #2e7d32;
  font-size: 15px;
}
.dialog-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #888;
  margin-bottom: 8px;
}
</style>
